import React, { useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { AppointmentStatus } from '@gopeerproject/chuck'
import { MediaProvider, useMediaContext } from '@gopeerproject/web-kit'

import { ClassroomEnvProvider } from '@components/ClassroomEnv'
import { useAuth } from '@hooks'
import { ClassroomManager, Heap, Intercom, Sentry } from '@helpers'

import {
  Classroom as ClassroomConstants,
  MemberControls as MC
} from '../../constants'
import { ClassroomPermissionsProvider, ClassroomProvider } from './contexts'
import {
  useClassroomActionOpen,
  useClassroomDispatch,
  useClassroomManager,
  useClassroomQuery,
  useClassroomState,
  useFilesLoader,
  useHideIntercomLauncher,
  useMemberControls
} from './hooks'
import Initializer from './components/Initializer'
import Classroom from './components/Classroom'
import ClassroomNotStarted from './components/ClassroomNotStarted'
import ClassroomClosed from './components/ClassroomClosed'
import AdmissionRejected from './components/AdmissionRejected'
import { registerCustomElements } from './components/Sketch/CustomElements'
import { useMemberControlsRealtimeActionsHandler } from './hooks/memberControls'

registerCustomElements()

const NEXT_CLASSROOM_URL = process.env.NEXT_CLASSROOM_URL

const ClassroomRoot = () => {
  const { step } = useClassroomState()
  const dispatch = useClassroomDispatch()
  const { room, localTracks } = useMediaContext()
  const { destroy } = useClassroomManager()
  const tracks = useRef(localTracks)
  useFilesLoader()

  const { mutateAsync: openClassroom } = useClassroomActionOpen()
  const { data: classroom, refetch: refetchClassroom } = useClassroomQuery({
    onSuccess: (classroom) => {
      if (classroom.type === 'group') {
        location.href = `${NEXT_CLASSROOM_URL}/session/${classroom.id}`
        return
      }

      if (classroom.mode === 'individual') {
        dispatch({
          type: 'data_fetched',
          payload: 'classroom'
        })
        return
      }

      switch (classroom.status) {
        case AppointmentStatus.InProgress: {
          if (classroom.twilioRoom?.sid) {
            dispatch({
              type: 'data_fetched',
              payload: 'classroom'
            })
            return
          }
          openClassroom().then(() => refetchClassroom())
          return
        }
        case AppointmentStatus.Confirmed:
          dispatch({
            type: 'set_step',
            payload: ClassroomConstants.STEPS.NOT_OPENED
          })
          return

        case AppointmentStatus.Pending:
        case AppointmentStatus.Completed:
        default:
          dispatch({
            type: 'set_step',
            payload: ClassroomConstants.STEPS.CLOSED
          })
      }
    }
  })
  useMemberControls({
    onSuccess: (data) => {
      if (data.status === MC.WaitingRoomStatus.REJECTED) {
        dispatch({
          type: 'set_step',
          payload: ClassroomConstants.STEPS.ADMISSION_REJECTED
        })
        return
      }
      dispatch({
        type: 'data_fetched',
        payload: 'memberControls'
      })
    }
  })
  useMemberControlsRealtimeActionsHandler()

  useEffect(() => destroy, [destroy])

  useEffect(() => {
    return () => {
      tracks.current.forEach((track) => track.stop())
    }
  }, [])

  useEffect(() => {
    return () => {
      if (room?.disconnect) room.disconnect()
    }
  }, [room])

  // TODO
  // once we have room style structure for demo classroom
  // following will not be required
  useEffect(() => {
    tracks.current = localTracks
  }, [localTracks])

  useEffect(() => {
    Intercom.update({
      'Classroom Issue Headline': null,
      'Classroom Issue Message': null,
      'Classroom Request Media Error': null,
      'Classroom Load Error': null,
      'Classroom Connnection Error': null,
      'Classroom Audio': null,
      'Classroom Video': null
    })
  }, [])

  useHideIntercomLauncher(step)

  switch (step) {
    case ClassroomConstants.STEPS.FETCHING:
      return <Initializer />
    case ClassroomConstants.STEPS.CLOSED:
      return <ClassroomClosed threadId={classroom.thread} />
    case ClassroomConstants.STEPS.NOT_OPENED:
      return <ClassroomNotStarted threadId={classroom.thread} />
    case ClassroomConstants.STEPS.ADMISSION_REJECTED:
      return <AdmissionRejected />
    default:
      return (
        <ClassroomPermissionsProvider>
          <Classroom />
        </ClassroomPermissionsProvider>
      )
  }
}

const ClassroomProviderWrapper = () => {
  const { profile } = useAuth()
  const { id } = useParams()

  const env =
    id === undefined
      ? ClassroomConstants.ENV.PLAYGROUND
      : ClassroomConstants.ENV.CLASSROOM

  return (
    <ClassroomEnvProvider env={env} id={id ?? profile.id}>
      <ClassroomProvider>
        <MediaProviderWrapper>
          <div className='gp-new-classroom'>
            <ClassroomRoot />
          </div>
        </MediaProviderWrapper>
      </ClassroomProvider>
    </ClassroomEnvProvider>
  )
}

const MediaProviderWrapper = ({ children }) => {
  const { profile } = useAuth()
  const classroomDispatch = useClassroomDispatch()
  const classroomState = useClassroomState()

  const onError = useCallback(
    (error, type) => {
      console.error(`PUBLICATION ERROR: ${error.message}`, error) // eslint-disable-line
      Heap.trigger(`Classroom - ${type} - Error`, {
        message: error.message,
        code: error.code
      })
      Intercom.update({ 'Classroom Load Error': error.message })
      Sentry.track(error)
      classroomDispatch({ type: 'set_error', payload: error })
    },
    [classroomDispatch]
  )

  const onRoomConnected = useCallback(
    (state) => ClassroomManager.updateParticipant(state),
    []
  )

  const onRoomDisconnected = useCallback(
    () =>
      ClassroomManager.updateParticipant({
        audio: false,
        video: false,
        screen: false
      }),
    []
  )

  const onBackgroundLoading = useCallback(
    (payload) =>
      classroomDispatch({
        type: 'set_is_background_loading',
        payload
      }),
    [classroomDispatch]
  )

  return (
    <MediaProvider
      onError={onError}
      id={profile.id}
      onRoomConnected={onRoomConnected}
      onRoomDisconnected={onRoomDisconnected}
      enableBackgrounds
      selectedBackground={classroomState.background}
      onBackgroundLoading={onBackgroundLoading}
    >
      {children}
    </MediaProvider>
  )
}

export default ClassroomProviderWrapper
